import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Button, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import './FollowList.css';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../components/Post';
import { apiService } from '../utils/api';

// フォロー/フォロワーリスト表示画面
function FollowList() {
  const { username, listType } = useParams(); // URLから username と listType (following/followers) を取得
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // URLパス分析からlistTypeを確保（followersまたはfollowing）
  const currentListType = listType || (location.pathname.includes('/followers') ? 'followers' : 'following');

  // アクセス権限をチェック
  const checkAccess = useCallback(() => {
    // ログインしていない場合はアクセス拒否
    if (!currentUser) {
      return false;
    }
    
    const isOwnProfile = currentUser.username === username;
    const isAdmin = currentUser.role === 'admin';
    
    // 自分自身のプロフィールまたは管理者の場合は常にアクセス可能
    if (isOwnProfile || isAdmin) {
      return true;
    }
    
    // ゲストユーザーは他人のフォロー情報を閲覧できない
    if (currentUser.role === 'guest') {
      return false;
    }
    
    // フォロワーリストとフォロー中リストは一般ユーザーでもアクセス可能
    return currentUser.role !== 'guest';
  }, [currentUser, username, currentListType]);

  // データ取得関数
  const fetchUserList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      
      
      // アクセス権限をチェック
      if (!checkAccess()) {
        setError('このページへのアクセス権限がありません');
        setLoading(false);
        return;
      }
      
      // ページタイトル設定
      if (currentListType === 'following') {
        setPageTitle(`${username}がフォロー中`);
      } else if (currentListType === 'followers') {
        setPageTitle(`${username}のフォロワー`);
      } else {
        // 不明なリストタイプの場合
        setPageTitle(`${username}のユーザーリスト`);
      }
      
      // APIからデータ取得（認証ヘッダーはapiServiceが自動で追加）
      const endpoint = currentListType === 'following' 
        ? `/api/social/following/${username}`
        : `/api/social/followers/${username}`;
        
      console.log('フォローリスト取得:', { 
        endpoint, 
        username, 
        currentListType,
        currentUser: currentUser ? {
          username: currentUser.username,
          role: currentUser.role
        } : null
      });
      
      const response = await apiService.get(endpoint, {
        // キャッシュ無効化
        params: { _t: new Date().getTime() }
      });
      
      console.log('フォローリスト取得成功:', { 
        endpoint, 
        responseData: response.data,
        hasFollowers: !!response.data?.followers,
        hasFollowing: !!response.data?.following 
      });
      
      
      
      if (!response.data) {
        
        setUsers([]);
        setLoading(false);
        return;
      }
      
      // 総数を取得し、ログに出力
      const totalCount = response.data?.total_count || response.data?.total || 0;
      
      
      // APIレスポンスのデータ構造を分析
      
      
      // ユーザーデータ取得（直接指定のキーを見る）
      let fetchedUsers = [];
      
      if (currentListType === 'followers' && Array.isArray(response.data.followers)) {
        fetchedUsers = response.data.followers;
        
      } else if (currentListType === 'following' && Array.isArray(response.data.following)) {
        fetchedUsers = response.data.following;
        
      } else {
        
        
        // 直接配列を取得できなかった場合の代替手段
        if (Array.isArray(response.data)) {
          fetchedUsers = response.data;
          
        } else if (typeof response.data === 'object') {
          // すべてのキーをチェックし、配列を探す
          const arrayKeys = Object.keys(response.data).filter(
            key => Array.isArray(response.data[key]) && response.data[key].length > 0
          );
          
          if (arrayKeys.length > 0) {
            const firstArrayKey = arrayKeys[0];
            fetchedUsers = response.data[firstArrayKey];
            
          }
        }
      }
      
      // ユーザーデータの検証と設定
      if (fetchedUsers && fetchedUsers.length > 0) {
        
        
        // 最初のユーザーの構造を確認
        
        
        // ユーザーIDが欠落している場合は追加
        const validatedUsers = fetchedUsers.map((user, index) => {
          if (!user.id) {
            return { ...user, id: `temp-id-${index}` };
          }
          return user;
        });
        
        setUsers(validatedUsers);
      } else {
        
        setUsers([]);
      }
      
    } catch (err) {
      console.error('フォローリスト取得エラー:', {
        endpoint,
        error: err,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      if (err.response && err.response.status === 401) {
        setError('認証が必要です。再度ログインしてください。');
      } else if (err.response && err.response.status === 403) {
        setError('このページへのアクセス権限がありません');
      } else if (err.response && err.response.status === 404) {
        setError(`ユーザー "${username}" が見つかりません`);
      } else {
        setError('データの取得に失敗しました: ' + (err.message || '不明なエラー'));
      }
      
      // エラー時は空のリストを設定
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [username, currentListType, checkAccess]);

  // コンポーネントマウント時とURLパラメータ変更時にデータを取得
  useEffect(() => {
    
    fetchUserList();
  }, [username, listType, currentListType, fetchUserList]);

  // フォロー/フォロー解除処理
  const handleFollowToggle = async (targetUsername, isFollowing) => {
    try {
      if (!currentUser) return;
      
      if (isFollowing) {
        // アンフォロー処理
        console.log('アンフォロー処理:', targetUsername);
        await apiService.delete(`/api/social/unfollow/${targetUsername}`, {
          params: { _t: Date.now() }
        });
      } else {
        // フォロー処理
        console.log('フォロー処理:', targetUsername);
        await apiService.post(`/api/social/follow/${targetUsername}`, {}, {
          params: { _t: Date.now() }
        });
      }
      
      // 状態を更新
      setUsers(users.map(user => {
        if (user.username === targetUsername) {
          return { ...user, is_following: !isFollowing };
        }
        return user;
      }));
      
      
      
    } catch (err) {
      
    }
  };

  // タブ切り替え処理
  const handleTabChange = (newListType) => {
    
    
    if (newListType !== currentListType) {
      // 新しいURLに移動
      navigate(`/profile/${username}/${newListType}`);
    }
  };

  // ユーザープロフィールへ移動
  const goToProfile = (targetUsername) => {
    navigate(`/profile/${targetUsername}`);
  };

  // 戻るボタン
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="follow-list__loading">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="follow-list__error">
        <div className="follow-list__header">
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <h2>エラー</h2>
        </div>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="follow-list">
      <div className="follow-list__header">
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <h2>{pageTitle}</h2>
      </div>
      
      <div className="follow-list__tabs">
        <div 
          className={`follow-list__tab ${currentListType === 'followers' ? 'follow-list__tab--active' : ''}`}
          onClick={() => handleTabChange('followers')}
        >
          フォロワー
        </div>
        {(currentUser && currentUser.username === username) || (currentUser && currentUser.role === 'admin') ? (
          <div 
            className={`follow-list__tab ${currentListType === 'following' ? 'follow-list__tab--active' : ''}`}
            onClick={() => handleTabChange('following')}
          >
            フォロー中
          </div>
        ) : null}
      </div>
      
      {users.length === 0 ? (
        <div className="follow-list__empty">
          {currentListType === 'following' ? 'フォローしているユーザーはいません' : 'フォロワーはいません'}
        </div>
      ) : (
        <div className="follow-list__users">
          {users.map((user, index) => (
            <div key={user.id || `user-${index}`} className="follow-list__user">
              <div 
                className="follow-list__user-info"
                onClick={() => goToProfile(user.username)}
              >
                <Avatar src={normalizeImageUrl(user.avatar)} className="follow-list__avatar" />
                <div className="follow-list__user-text">
                  <div className="follow-list__display-name">{user.display_name || user.username}</div>
                  <div className="follow-list__username">@{user.username}</div>
                  {user.bio && <div className="follow-list__bio">{user.bio}</div>}
                </div>
              </div>
              
              {currentUser && currentUser.username !== user.username && (
                <Button
                  variant="outlined"
                  size="small"
                  className={`follow-list__button ${user.is_following ? 'follow-list__button--following' : ''}`}
                  onClick={() => handleFollowToggle(user.username, user.is_following)}
                  startIcon={user.is_following ? <CheckIcon /> : <PersonAddIcon />}
                >
                  {user.is_following ? 'フォロー中' : 'フォロー'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FollowList; 